import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faKey, faPlusCircle, faPowerOff } from '@fortawesome/free-solid-svg-icons';
import { trackPromise } from 'react-promise-tracker';
import Swal from 'sweetalert2';
import caAPI from '../../../services/caAPI';
import DommusBarraTitulo from '../../../components/DommusBarraTitulo';
import ItemBarraTitulo from '../../../components/DommusBarraTitulo/ItemBarraTitulo';
import DommusTabelaPaginadaControlada from '../../../components/DommusTabelaPaginadaControlada';
import { successToast } from '../../../components/DommusToast';
import ModalAgenteIa, { ModalRotacaoChaveAgenteIa } from './ModalAgenteIa';
import { extrairDados, obterMensagemErro } from './utils';

function obterNomeModelo(agente, modelosPorSlug) {
  return agente?.modelo_metadados?.nome || modelosPorSlug[agente.modelo] || agente.modelo;
}

function obterModelosPorSlug(modelos) {
  return modelos.reduce((resultado, modelo) => {
    resultado[modelo.slug] = modelo.nome;
    return resultado;
  }, {});
}

export default function GestaoAgentesIa() {
  const [listaAgentes, setListaAgentes] = useState([]);
  const [modelosPorSlug, setModelosPorSlug] = useState({});
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(15);
  const [totalAgentes, setTotalAgentes] = useState(0);
  const [openModalAgente, setOpenModalAgente] = useState(false);
  const [openModalRotacao, setOpenModalRotacao] = useState(false);
  const [agenteSelecionado, setAgenteSelecionado] = useState(null);
  const [agenteRotacao, setAgenteRotacao] = useState(null);

  const handleCarregarAgentes = useCallback(() => {
    return trackPromise(
      Promise.all([
        caAPI.agenteIa.list({ page: pagina, per_page: tamanhoPagina }),
        caAPI.agenteIa.listarModelos(),
      ])
        .then(([respostaAgentes, respostaModelos]) => {
          const agentes = respostaAgentes?.data?.data || [];
          const modelos = extrairDados(respostaModelos);

          setListaAgentes(agentes);
          setModelosPorSlug(obterModelosPorSlug(Array.isArray(modelos) ? modelos : []));
          setTotalAgentes(respostaAgentes?.data?.total || 0);
        })
        .catch(() => {
          Swal.fire({
            titleText: 'Erro',
            text: 'Erro ao carregar agentes.',
            icon: 'error',
          });
        })
    );
  }, [pagina, tamanhoPagina]);

  useEffect(() => {
    handleCarregarAgentes();
  }, [handleCarregarAgentes]);

  function handleAlterarTamanhoPagina(novoTamanho) {
    setPagina(1);
    setTamanhoPagina(Number(novoTamanho) || 15);
  }

  function handleAbrirNovoAgente() {
    setAgenteSelecionado(null);
    setOpenModalAgente(true);
  }

  function handleEditarAgente(agente) {
    trackPromise(
      caAPI.agenteIa.buscarPorId(agente.id_agente_ia)
        .then(resposta => {
          setAgenteSelecionado(extrairDados(resposta));
          setOpenModalAgente(true);
        })
        .catch(erro => Swal.fire({
          titleText: 'Erro',
          text: obterMensagemErro(erro, 'Erro ao carregar agente.'),
          icon: 'error',
        }))
    );
  }

  function handleFecharModalAgente() {
    setOpenModalAgente(false);
    setAgenteSelecionado(null);
  }

  function handleAgenteSalvo(agenteSalvo) {
    handleFecharModalAgente();
    if (agenteSalvo?.id_agente_ia && agenteSelecionado) {
      // Edição: upsert local para evitar reload desnecessário
      setListaAgentes(atuais => atuais.map(a =>
        Number(a.id_agente_ia) === Number(agenteSalvo.id_agente_ia) ? agenteSalvo : a
      ));
    } else {
      // Criação: prepend local e incrementa total para manter paginação consistente
      setListaAgentes(atuais => [agenteSalvo, ...atuais]);
      setTotalAgentes(total => total + 1);
    }
  }

  function handleAbrirRotacao(agente) {
    setAgenteRotacao(agente);
    setOpenModalRotacao(true);
  }

  function handleFecharRotacao() {
    setOpenModalRotacao(false);
    setAgenteRotacao(null);
  }

  function handleChaveRotacionada(idAgenteIa, idChaveApi) {
    setListaAgentes(agentesAtuais => agentesAtuais.map(agente => (
      agente.id_agente_ia !== undefined && Number(agente.id_agente_ia) === Number(idAgenteIa)
        ? { ...agente, id_chave_api: idChaveApi }
        : agente
    )));
  }

  function handleAlterarEstado(agente) {
    const ativoDesejado = !agente.ativo;
    const acao = ativoDesejado ? 'ativar' : 'inativar';

    Swal.fire({
      titleText: `Confirmar ${acao}?`,
      text: `Deseja ${acao} o Agente "${agente.nome}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Sim, ${acao}`,
      cancelButtonText: 'Cancelar',
    }).then(resultado => {
      if (!resultado.isConfirmed) return;

      let requisicao;
      try {
        requisicao = caAPI.agenteIa.alterarEstado(agente.id_agente_ia, ativoDesejado);
      } catch (erro) {
        Swal.fire({ titleText: 'Erro', text: 'Erro inesperado ao alterar estado do agente.', icon: 'error' });
        return;
      }

      trackPromise(
        requisicao
          .then(() => {
            successToast.fire({ text: `Agente ${ativoDesejado ? 'ativado' : 'inativado'} com sucesso.` });
            return handleCarregarAgentes();
          })
          .catch(erro => Swal.fire({
            titleText: 'Erro',
            text: obterMensagemErro(erro, `Erro ao ${acao} agente.`),
            icon: 'error',
          }))
      );
    });
  }

  const cabecalhoTabela = (
    <tr>
      <th>Nome</th>
      <th>Modelo</th>
      <th>Estado</th>
      <th className="text-center">Ações</th>
    </tr>
  );

  const linhasTabela = listaAgentes.map(agente => (
    <tr key={agente.id_agente_ia}>
      <td>{agente.nome}</td>
      <td>{obterNomeModelo(agente, modelosPorSlug)}</td>
      <td>
        <Badge variant={agente.ativo ? 'success' : 'secondary'}>
          {agente.ativo ? 'Ativo' : 'Inativo'}
        </Badge>
      </td>
      <td className="text-center">
        <ButtonGroup size="sm">
          <Button variant="outline-primary" type="button" title="Editar Agente" aria-label={`Editar ${agente.nome}`} onClick={() => handleEditarAgente(agente)}>
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button
            variant={agente.ativo ? 'outline-secondary' : 'outline-success'}
            type="button"
            title={agente.ativo ? 'Inativar Agente' : 'Ativar Agente'}
            aria-label={`${agente.ativo ? 'Inativar' : 'Ativar'} ${agente.nome}`}
            onClick={() => handleAlterarEstado(agente)}
          >
            <FontAwesomeIcon icon={faPowerOff} />
          </Button>
          <Button
            variant="outline-warning"
            type="button"
            title="Rotacionar chave"
            aria-label={`Rotacionar chave de ${agente.nome}`}
            onClick={() => handleAbrirRotacao(agente)}
          >
            <FontAwesomeIcon icon={faKey} />
          </Button>
        </ButtonGroup>
      </td>
    </tr>
  ));

  return (
    <Container fluid>
      <DommusBarraTitulo>
        <ItemBarraTitulo>Agentes de IA</ItemBarraTitulo>
        <OverlayTrigger
          key="overlay-novo-agente"
          overlay={<Tooltip id="tooltip-novo-agente">Adicionar novo agente</Tooltip>}
          placement="top"
        >
          <ItemBarraTitulo classe="btn py-3" onClick={handleAbrirNovoAgente}>
            <FontAwesomeIcon icon={faPlusCircle} color="#4caf50" size="lg" />
          </ItemBarraTitulo>
        </OverlayTrigger>
      </DommusBarraTitulo>

      <div className="mt-3">
        {listaAgentes.length ? (
          <DommusTabelaPaginadaControlada
            linhasCabecalho={cabecalhoTabela}
            linhasCorpo={linhasTabela}
            optionsTabela={{}}
            pagina={pagina}
            setPagina={setPagina}
            tamanhoPagina={tamanhoPagina}
            setTamanhoPagina={handleAlterarTamanhoPagina}
            tamanhoTotal={totalAgentes}
          />
        ) : (
          <Alert variant="info" className="mb-0">
            Nenhum agente cadastrado. Clique em 'Novo Agente' para começar.
          </Alert>
        )}
      </div>

      <ModalAgenteIa
        aberto={openModalAgente}
        close={handleFecharModalAgente}
        agente={agenteSelecionado}
        onSalvo={handleAgenteSalvo}
      />
      <ModalRotacaoChaveAgenteIa
        aberto={openModalRotacao}
        agente={agenteRotacao}
        close={handleFecharRotacao}
        onRotacionada={handleChaveRotacionada}
      />
    </Container>
  );
}
